import React, { useEffect, useState } from "react";
import {
  Flex, Icon, Image, Text, useColorModeValue, Link, Button, IconButton,
  Tooltip, useClipboard, Select, Box, Checkbox, Stack, Badge,
  HStack, VStack
} from "@chakra-ui/react";
import Card from "components/card/Card.js";
import { StarIcon } from "@chakra-ui/icons";
import { MdCheckCircle, MdCancel, MdShoppingCart, MdContentCopy, MdDelete, MdAlbum, MdOutlineError, MdCalendarToday } from "react-icons/md";
import { IoMdAlert } from "react-icons/io";
import Title  from "services/Title";
import Amazon from 'assets/img/amazon.ico';
import Flipkart from 'assets/img/flipkart.ico';
import Snapdeal from 'assets/img/snapdeal.ico';
import Meesho from 'assets/img/meesho.ico';
import Glowroad from 'assets/img/glowroad.ico';
import Shopsy from 'assets/img/shopsy.ico';
import freeup from 'assets/img/freeup.ico';
import imageNotFound from 'assets/img/Image_not_available.png';
import RelevanceBadge from 'components/badge/RelevanceBadge';
import { toLower } from "lodash";
import CommentPopover from "./CommentPopover";

// --- Internal Helper Components ---

const getStatusConfig = (type, val) => {
  const configs = {
    whiteList: { color: "green", icon: MdCheckCircle, label: "WHITELIST" },
    rejected: { color: "green", icon: MdCheckCircle, label: "REJECTED" }, // Added this
    test: { color: "blue", icon: MdShoppingCart, label: "TEST PURCHASE" },
    blackList: { color: "red", icon: MdCancel, label: "REPORT SELLER" },
    delete: { color: "orange", icon: MdDelete, label: "REMOVE" },
  };
  return configs[val] || { color: "gray", icon: MdAlbum, label: "SELECT" };
};

const StatusOverlay = ({ toggles }) => {
  const activeAction = Object.keys(toggles).find((key) => toggles[key] === true);
  if (!activeAction) return null;

  const config = {
    whiteList: { icon: MdCheckCircle, color: "green.500", label: "WHITELIST" },
    rejected: { icon: MdCheckCircle, color: "green.500", label: "REJECTED" },
    test: { icon: MdShoppingCart, color: "blue.500", label: "TEST" },
    blackList: { icon: MdCancel, color: "red.500", label: "REPORT" },
    delete: { icon: MdDelete, color: "orange.500", label: "DELETE" },
  };

  const { icon, color, label } = config[activeAction];

  return (
    <Box
      position="absolute"
      top="0" left="0" right="0" bottom="0"
      bg="blackAlpha.700"
      borderRadius="xl"
      zIndex="2"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      transition="all 0.2s"
    >
      <Icon as={icon} color="white" w={10} h={10} mb={2} />
      <Text color="white" fontWeight="bold" fontSize="xs" letterSpacing="wider">
        {label}
      </Text>
    </Box>
  );
};


const getIcon = (iconTag, isActive, size = 20) => {
  const inactiveGray = "#A0AEC0"
  return (
    <Icon
      as={iconTag}
      color={isActive ? "white" : inactiveGray}
      boxSize={`${size}px`} // This increases the actual icon size
    />

  )
};

// --- New: Formats and displays the detection date consistently across both views ---
const DetectedDateBadge = ({ date, variant = "grid" }) => {
  if (!date) return null;

  const formatDate = (d) => {
    const parsed = new Date(d);
    if (isNaN(parsed.getTime())) return d; // fall back to raw value if unparsable
    return parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };


  return (
    <Badge
      colorScheme="purple"
      variant="subtle"
      fontSize="9px"
      px={2}
      py={1}
      borderRadius="md"
      display="flex"
      alignItems="center"
      gap="4px"
      w="fit-content"
    >
      <Icon as={MdCalendarToday} boxSize="10px" />
      Detected: {formatDate(date)}
    </Badge>
  );
};

const ActionButtons = ({ product, isAdmin, handlers, toggles, layout }) => {
  // Use a specific hex or gray shade to ensure it's not pure black

  return (
    <Flex gap={2} align="center" wrap="wrap">

      <Button
        variant="solid"
        colorScheme="red"
        // minW="150px"
        p="1"
        pr="1.5"
        // gap={1}
        leftIcon={<Icon as={MdOutlineError}/>}
        onClick={() => handlers.onBlackList(product)}
      >
        Report 
      </Button>
      <Button
        variant="outline"
        colorScheme="green"
        // minW="100px"
         p="1"
        gap={1}
        leftIcon={<Icon as={MdCheckCircle} mr="-2" />}
        onClick={() => handlers.onWhiteList(product)}
      >
        Whitelist
      </Button>
      {layout == "list" &&
        (<>
          <Tooltip label='Test Purchase'>
            <IconButton
              size="md"
              variant={toggles.test ? "solid" : "ghost"}
              colorScheme={toggles.test ? "blue" : "gray"}
              icon={getIcon(MdShoppingCart, toggles.test)}
              onClick={() => handlers.onTest(product)}
            />
          </Tooltip>

          <Tooltip label='Delete Product'>
            <IconButton
              size="md"
              variant={toggles.delete ? "solid" : "ghost"}
              colorScheme={toggles.delete ? "red" : "gray"}
              icon={getIcon(MdDelete, toggles.delete)}
              onClick={() => handlers.onDelete(product)}
            />
          </Tooltip>

        </>)}
    </Flex>
  );
};

const ListView = ({ product, isSelected, onSelect, isAdmin, handlers, toggles, common,currentRelevance,handleRelevanceChange }) => {
  const { textColor, renderStars, chooseAbuseType, getPlatformIcon, abuseTypes, handleAbuseType, onCopy, currentAbuseId } = common;

  // 1. Determine Active State
  const activeKey = Object.keys(toggles).find(k => toggles[k] === true);
  const statusInfo = getStatusConfig(activeKey);

  // 2. Dynamic Styles
  const borderColor = activeKey ? `${statusInfo.color}.400` : "transparent";

  // Manual color mapping to avoid hook errors in loops, or use simple hex if preferred
  // Using generic props here for safety
  const bgStyle = activeKey
    ? { bg: `${statusInfo.color}.50`, _dark: { bg: `rgba(0,0,0,0.2)` } }
    : { bg: "white", _dark: { bg: "navy.800" } };

  return (
    <Card
      p="13px"
      mb="3px"
      position="relative"
      overflow="hidden"
      borderLeftWidth={activeKey ? "5px" : "0px"}
      borderLeftColor={borderColor}
      bg={bgStyle.bg}
      _dark={bgStyle._dark}
      transition="all 0.2s"
      boxShadow="sm"
      _hover={{ boxShadow: "md" }}
    >
      <Flex align="center" gap={4}>

        {/* Checkbox */}
        {isAdmin && (
          <Checkbox isChecked={isSelected} onChange={onSelect} colorScheme="brand" mr={2} />
        )}

        {/* Image Section */}
        <Box position="relative" w="100px" h="100px" flexShrink={0}>
          <Link href={product.prod_link} isExternal w='100%' h='100%' display="block">
            <Image src={product.image_link} w="100%" h="100%" objectFit="contain" />
          </Link>
          <Box position='absolute' bottom='0' left='0' bg="white" _dark={{ bg: "navy.900" }} borderRadius="tr-md" p="1" shadow="sm">
            <Image src={getPlatformIcon(product.platform_id)} w='16px' />
          </Box>
        </Box>

        {/* Content Section */}
        <Box flex="1">
          {/* Row 1: Title + Status Badge */}
          <Flex align="center" mb={1} wrap="wrap">
            <Link href={product.prod_link} isExternal fontWeight="bold" fontSize="md" color={textColor} mr={2}>
              <Title product={product} /> 
            </Link>
            {activeKey && (
              <Badge colorScheme={statusInfo.color} variant="solid">
                {statusInfo.label}
              </Badge>
            )}
          </Flex>

          {/* Row 2: Keyword + Rating + Detected Date + Relevance */}
          <Flex gap={4} mb={2} fontSize="xs" color="gray.500" align="center">
            <Text>{product.keyword}</Text>
            <Flex hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>
              {renderStars(product.overall_rating)}
            </Flex>
            <DetectedDateBadge date={product.date_detected} variant="grid" />
              <RelevanceBadge
                  value={currentRelevance}
                  editable="true"
                  onChange={handleRelevanceChange}
              />
          </Flex>

          {/* Row 3: Price + Code + Seller */}
          <Flex gap={4} align="center" fontSize="sm" wrap="wrap" mb={2}>
            <Text fontWeight="bold" color={textColor} hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>
              Rs. {product.selling_price}
            </Text>

            <HStack>
              <Text color="gray.500">Code: {product.prod_code}</Text>
              <IconButton size='xs' icon={<MdContentCopy />} onClick={onCopy} variant='ghost' aria-label="Copy" />
            </HStack>

            <Text color="gray.500" hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>
              Seller: <Text as="span" fontWeight="semibold" color={textColor}>{product.seller_name}</Text>
            </Text>
          </Flex>

          {/* Row 4: Abuse Selector (Specific to Suspicious Item) */}
          {isAdmin && (
            <Select
              size="xs"
              w="150px"
              variant="outline"
              borderColor="gray.200"
              value={currentAbuseId ?? ""}
              onChange={(e) => handleAbuseType(e.target.value)}
              bg="white"
              _dark={{ bg: "navy.900" }}
            >
              {(currentAbuseId ?? "") === "" && <option key="" value="">Not Selected</option>}
              {abuseTypes?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.abuse_type} abuse
                </option>
              ))}
            </Select>
          )}
        </Box>
<Box
    position="absolute"
    top="8px"
    right={isAdmin ? "42px" : "8px"}
    zIndex={4}
>
    <CommentPopover
        product={product}
        isAdmin={isAdmin}
        onSave={common.onSaveComments}
        size="xs"
        compact
    />
</Box>
        {/* Action Buttons Section */}
        {isAdmin && (
          <Box>
            <ActionButtons product={product} isAdmin={isAdmin} handlers={handlers} toggles={toggles} />
          </Box>
        )}

      </Flex>
    </Card>
  );
};

const GridView = ({ product, isSelected, onSelect, isAdmin, handlers, toggles, common,currentRelevance,handleRelevanceChange }) => {
  const { textColor, subTextColor, borderColor, getPlatformIcon, renderStars, onCopy, hasCopied, currentAbuseId } = common;

  // Determine which action is currently "Active" for this specific product
  const activeKey = Object.keys(toggles).find(k => toggles[k] === true);
  const statusInfo = getStatusConfig(null, activeKey);

  return (
    <Card
      p='5px'
      bg={useColorModeValue("white", "navy.800")}
      transition='0.3s cubic-bezier(.25,.8,.25,1)'
      _hover={{ shadow: "lg", transform: "translateY(-4px)" }}
      border={isSelected || activeKey ? "2px solid" : "1px solid"}
      borderColor={activeKey ? `${statusInfo.color}.500` : isSelected ? "brand.500" : borderColor}
      overflow='hidden'
      h="100%"
      display="flex"
      flexDirection="column"
    >
      <Box position='relative' bg='white' p='15px' h='200px' borderBottom="1px solid" borderColor={borderColor} borderRadius="xl" overflow="hidden">
        <StatusOverlay toggles={toggles} />
      
        <Link href={product.prod_link} isExternal w='100%' h='100%' display="block">
          <Image src={product.image_link} w='100%' h='100%' objectFit='contain' />
        </Link>

        <Box position='absolute' top='10px' left='10px' bg="white" borderRadius="md" p="1" shadow="sm" border="1px solid" borderColor="gray.100">
          <Image src={getPlatformIcon(product.platform_id)} w='18px' h='18px' />
        </Box>

        {isAdmin && (
          <>
            <Checkbox
              position="absolute" top="12px" right="12px" bg={"white"}
              isChecked={isSelected} onChange={onSelect} colorScheme="brand" size="lg" zIndex={3}
            />
            <Flex flexDirection={"column"} position="absolute" top="40px" right="3px" backgroundColor={"white"} zIndex={3} rounded={5} p={1}>

              <Tooltip label='Test Purchase'>
                <IconButton
                  size="sm"
                  variant={toggles.test ? "solid" : "ghost"}
                  colorScheme={toggles.test ? "blue" : "gray"}
                  icon={getIcon(MdShoppingCart, toggles.test, 18)}
                  onClick={() => handlers.onTest(product)}
                />
              </Tooltip>

              <Tooltip label='Delete Product'>
                <IconButton
                  size="sm"
                  variant={toggles.delete ? "solid" : "ghost"}
                  colorScheme={"red"}
                  icon={getIcon(MdDelete, toggles.delete, 18)}
                  onClick={() => handlers.onDelete(product)}
                />
              </Tooltip>
            </Flex>
          </>
        )}
      </Box>

      <VStack px='15px' align='start' spacing='8px' flex='1'>
        <Link href={product.prod_link} isExternal w="100%">
          <Text color={textColor} fontWeight='700' fontSize='sm' noOfLines={2} lineHeight="1.4" h="40px">
            {product.title}
          </Text>
        </Link>

           <HStack w="100%" justify="space-between">
                                {!product.seller_name?.toLowerCase().startsWith("xxxx") && (
                                    <HStack spacing="1px">
                                    {renderStars(product.overall_rating)}
                                    </HStack>
                                )}
        
                                <Box ml="auto">
                                      <RelevanceBadge
                                          value={currentRelevance}
                                          editable="true"
                                          onChange={handleRelevanceChange}
                                      />                               
                                      </Box>
                                </HStack>

        <HStack w="100%" justify="space-between">
          {/* <Badge variant="subtle" colorScheme="brand" fontSize="10px">
            {product.keyword || "General"}
          </Badge> */}
          <Text color={textColor} fontWeight='900' fontSize='xl' hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>₹{product.selling_price < 0 ? "N/A" : product.selling_price}</Text>
          <DetectedDateBadge date={product.date_detected} variant="grid" />

        </HStack>


        <VStack w='100%' align="start" spacing={1} bg={useColorModeValue("gray.50", "whiteAlpha.100")} p="2" borderRadius="md">
          <Text fontSize='13px' style={{ color: "gray" }} noOfLines={1} hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>
            Seller: <b style={{ color: "inherit" }}>{product.seller_name}</b>
          </Text>
          <HStack w="100%" justify="space-between">
            <Text fontSize='13px' style={{ color: "gray" }}>Code: <b>{product.prod_code}</b></Text>
            <IconButton size='xs' icon={<MdContentCopy />} onClick={onCopy} variant='ghost' h="20px" />
          </HStack>
        </VStack>
      </VStack>
        <VStack spacing={0} w="100%">
      {isAdmin && (
          <>          <Select
            variant="unstyled" size="sm" textAlign="center" fontSize="xs" fontWeight="bold"
            p="2" border="1px solid" borderColor={"blue.200"} rounded={5}
            value={currentAbuseId ?? ""}
            onChange={(e) => common.handleAbuseType(e.target.value)}
          >
            {(currentAbuseId ?? "") === "" && <option value="">Not Selected</option>}
            {common.abuseTypes?.map((item) => (
              <option key={item.id} value={item.id}>
                {item.abuse_type} abuse
              </option>
            ))}
          </Select>
          <ActionButtons product={product} handlers={handlers} toggles={toggles} isSelected={isSelected} />
          </>

      )}
        <Box
    position="absolute"
    top="8px"
    right={isAdmin ? "42px" : "8px"}
    zIndex={4}
>
          <CommentPopover
              product={product}
              isAdmin={isAdmin}
              onSave={common.onSaveComments}
              size="xs"
              compact
          />
      </Box>
              </VStack>

    </Card>
  );
};

// --- Main Export ---

export default function SuspiciousItem(props) {
  const { product, viewMode, isSelected, onSelect, handleInsertRelevance,handleInsertSeller, removeFromInsert, isAdmin, abuseTypes, handleInsertAbuse, setNoAbuseProducts } = props;

  const [toggles, setToggles] = useState({ whiteList: false, test: false, blackList: false, delete: false, rejected: false });
  const textColor = useColorModeValue("brands.900", "white");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.100");

  const { onCopy } = useClipboard(product.prod_code);
  const [currentAbuseId, setCurrentAbuseId] = useState(product.abuse_type_id ?? "");
  const [localComments, setLocalComments] = useState(product.comments || []);
const [currentRelevance, setCurrentRelevance] = useState(
    product.relevance || "SELECT"
);

useEffect(() => {
    setCurrentRelevance(product.relevance || "SELECT");
}, [product.relevance]);

useEffect(() => {
  setLocalComments(product.comments || []);
}, [product.comments, product.pd_id]);

  // Logic Handlers
  const handleAction = async (type, status) => {
    const key = type === 'W' ? 'whiteList' : type === 'T' ? 'test' : type === 'R' ? 'blackList' : 'delete';
    const active = !toggles[key];

    setToggles({ whiteList: false, test: false, blackList: false, delete: false, [key]: active });



    if (active) await handleInsertSeller(product, type);
    else await removeFromInsert(product, type);
  };

  const handleRelevanceChange = (relevance) => {

    setCurrentRelevance(relevance);

    handleInsertRelevance(product, relevance);

};
  useEffect(() => {
    setCurrentAbuseId(product.abuse_type_id ?? "");
  }, [product.abuse_type_id]);

  const handleSaveComments = async (comments) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${backendUrl}/api/products/updateComments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ products: [{ pd_id: product.pd_id, comments }] }),
    });

    if (!response.ok) {
      throw new Error("Failed to save comments");
    }

    setLocalComments(comments);
  };

  const commonProps = {
    textColor,
    borderColor,
    abuseTypes,
    onCopy,
    currentAbuseId,
    onSaveComments: handleSaveComments,

    getPlatformIcon: (id) => {
      switch (id) {
        case 1:
          return Amazon; // Use imported Amazon icon
        case 2:
          return Flipkart; // Use imported Flipkart icon
        case 3:
          return Snapdeal;
        case 4:
          return Meesho;// Use imported Snapdeal icon
        case 5:
          return Glowroad;
        case 6:
          return Shopsy;
        case 7:
          return freeup;
        default:
          return imageNotFound;
      }
    },

    renderStars: (rating) => {
      rating = rating < 0 ? 0 : rating
      const filledStars = Math.floor(rating);
      const hasHalfStar = rating % 1 >= 0.5;
      const totalStars = filledStars + (hasHalfStar ? 1 : 0);
      const stars = [];

      for (let i = 0; i < filledStars; i++) {
        stars.push(<Icon as={StarIcon} color="gold" key={`star-${i}`} />);
      }

      if (hasHalfStar) {
        stars.push(<Icon as={StarIcon} color="gold" key={`half-star`} />);
      }

      const emptyStars = 5 - totalStars;
      for (let i = 0; i < emptyStars; i++) {
        stars.push(<Icon as={StarIcon} color="gray.300" key={`empty-star-${i}`} />);
      }

      return stars;
    },

    chooseAbuseType: () => {
      // Handle explicit "None" cases
      if (!product.abuse_type_id || product.abuse_type_id === 0) return 'None';

      // Find the abuse type object in the provided array
      const found = abuseTypes?.find(type => type.id === product.abuse_type_id);

      // Return label or fallback
      return found ? found.abuse_type : 'Choose Type';
    },
    handleAbuseType: async (abuse_type) => {
      const normalizedAbuseType = abuse_type === "" ? "" : Number(abuse_type);

      if (normalizedAbuseType === "" && (currentAbuseId ?? "") !== "") {
        return;
      }

      setCurrentAbuseId(normalizedAbuseType);
      if (normalizedAbuseType !== "") {
        await handleInsertAbuse(product, normalizedAbuseType);
      }

      if (normalizedAbuseType === 0) {
        setNoAbuseProducts((prev) => {
          if (!prev.some((p) => p.psm_id === product.psm_id)) {
            return [{ ...product, abuse_type_id: 0 }, ...prev];
          }
          return prev;
        });
      } else {
        setNoAbuseProducts((prev) => prev.filter((p) => p.psm_id !== product.psm_id));
      }
    }
  };

  const handlers = {
    onWhiteList: () => handleAction('W'),
    onTest: () => handleAction('T'),
    onBlackList: () => handleAction('R'),
    onDelete: () => handleAction('X'),
    onNoAbuseList: () => commonProps.handleAbuseType(0),
    //   onReject: async (prod) => {
    //   // 1. Set the UI state so the card looks selected
    //   setToggles({ whiteList: false, test: false, blackList: false, delete: false, rejected: true });

    //   // 2. Perform the actual logic
    //   if (prod.seller_name?.toLowerCase().startsWith("xxxx")) {
    //     await commonProps.handleAbuseType(0);
    //   } else {
    //     await handleInsertSeller(prod, 'W');
    //   }

    // }
  };

  const cardProduct = { ...product, comments: localComments };

  return viewMode === 'grid'
    ? <GridView {...props} product={cardProduct} toggles={toggles} handlers={handlers} common={commonProps}   
                currentRelevance={currentRelevance} 
              handleRelevanceChange={handleRelevanceChange} />
    : <ListView
      {...props}
      product={cardProduct}
      toggles={toggles}
      handlers={handlers}
      common={commonProps}
      currentRelevance={currentRelevance}
      handleRelevanceChange={handleRelevanceChange}
  />;
}